/* External */
import {
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

/* Ott-common */
import { SectionId } from '@dtvgo/backend-service/normalize';

/* Storage */
import { fetchCarousel } from '../storage/carousels';
import { EPG_FILTERS_ID, SOURCE_CAROUSEL_ID, STATUS_TYPES } from '../utils/constants';

/* Utils */
import useCarousel from './useCarousel';
import useRequestAndDelete from './useRequestAndDelete';
import useRefSync from './useRefSync';

export default function useFetchCarousel({
  sourceCarouselId = undefined,
  elementId = undefined,
  type,
  source,
  filters,
  avoidFetch = false,
  sourceProvider,
  max,
  isCheddar = false,
  recommendationType = undefined,
  // Use it to force reload the carousel: provide a different value each time you need
  // section to be reloaded.
  reloader,
  onFetched,
}) {
  const sourceCarouselElementId = sourceCarouselId || elementId;
  const [requestId, setRequestId] = useState();
  const wasFetchDispatched = useRef(false);
  const carousel = useCarousel(sourceCarouselElementId, filters);
  const request = useRequestAndDelete(requestId);
  const onFetchedRef = useRefSync(onFetched);

  const isFetching = !carousel
    || (request ? request.status === STATUS_TYPES.INIT_STATE : !avoidFetch);
  const onFetchedCalled = useRef(false);

  useEffect(() => {
    wasFetchDispatched.current = false;
  }, [sourceCarouselElementId, reloader, filters]);

  const dispatch = useDispatch();
  useEffect(() => {
    if (
      !avoidFetch
      && !wasFetchDispatched.current
      // TODO: Review this condition
      && ((sourceCarouselElementId && type && source)
        || sourceCarouselElementId === SectionId.MOST_WATCHED
        || sourceCarouselElementId === EPG_FILTERS_ID.RECENTLY_WATCHED
        || sourceCarouselElementId === SOURCE_CAROUSEL_ID.MY_LIST)
    ) {
      onFetchedCalled.current = false;
      const action = fetchCarousel({
        sourceCarouselId: sourceCarouselElementId,
        source,
        sourceProvider,
        filters,
        type,
        max,
        isCheddar,
        recommendationType,
      });
      setRequestId(action.requestId);
      dispatch(action);
      wasFetchDispatched.current = true;
    }
  }, [
    avoidFetch,
    source,
    dispatch,
    sourceCarouselElementId,
    sourceProvider,
    reloader,
    type,
    max,
    filters,
    isCheddar,
    recommendationType,
  ]);

  useEffect(() => {
    if (!isFetching && !onFetchedCalled.current) {
      onFetchedCalled.current = true;
      if (onFetchedRef.current) {
        onFetchedRef.current(sourceCarouselElementId, carousel);
      }
    }
  }, [carousel, isFetching, onFetchedRef, sourceCarouselElementId]);

  return {
    carousel,
    isFetching,
  };
}
